/**
 * Afuna Aromatics — Collection "Shop by Ritual" selector.
 *
 * R3 founder-review fix (single source of truth):
 * One function, setPathway(state), drives every active element together —
 * the three native radios (kept for no-JS fallback + keyboard state),
 * [data-active-pathway] on the section root (CSS keys the active pill,
 * pathway image, guidance copy, and the two mapped blend cards off it),
 * a polite screen-reader announcement, and — on mobile only, and only from
 * an explicit customer action — a smooth move to that pathway's FIRST
 * blend card. The previous build let CSS :checked ~ drive the visuals
 * while a separate change handler did the announce/scroll; on mobile the
 * image and cards could lag the selected pill. The :checked ~ rules are
 * now the no-JS fallback only (:not([data-active-pathway])).
 *
 * Progressive enhancement: with JS disabled the native radio/label
 * semantics + the gated :checked ~ rules keep the selector working,
 * Release selected by default. Reduced motion uses instant movement.
 */
(function () {
  var MOBILE_MAX_WIDTH = '(max-width: 899.98px)';
  var STATES = ['release', 'remember', 'reign'];

  function enhance(root) {
    var radios = root.querySelectorAll('.aa-ritual-selector__radio');
    if (!radios.length) return;

    var reduceMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

    var live = document.createElement('div');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('role', 'status');
    live.style.cssText =
      'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
    root.appendChild(live);

    var current = (function () {
      for (var i = 0; i < radios.length; i++) {
        if (radios[i].checked) return radios[i].value;
      }
      return 'release';
    })();

    var readyForScroll = false;

    function moveToFirstCard(state) {
      if (!readyForScroll) return;
      // R4 item 5: scroll to the pathway's first blend card on BOTH mobile
      // and desktop after an explicit interaction (never on initial load —
      // guarded by readyForScroll). Reduced motion uses instant movement;
      // the sticky-header offset comes from scroll-margin-top on the cardset.
      var set = root.querySelector('.aa-ritual-selector__cardset--' + state);
      if (!set) return;
      var target = set.querySelector('.aa-ritual-card') || set;
      target.scrollIntoView({ behavior: reduceMotionMQ.matches ? 'auto' : 'smooth', block: 'start' });
    }

    /* ---------- THE single source of truth ---------- */
    function setPathway(state, opts) {
      opts = opts || {};
      if (STATES.indexOf(state) === -1) return;
      var changed = state !== current || opts.force === true;
      current = state;

      radios.forEach(function (r) {
        if (r.value === state) {
          if (!r.checked) r.checked = true;
        } else if (r.checked) {
          r.checked = false;
        }
      });

      root.setAttribute('data-active-pathway', state);

      if (changed && opts.force !== true) {
        var guidance = root.querySelector('.aa-ritual-selector__guidance-text--' + state);
        var label = state.charAt(0).toUpperCase() + state.slice(1);
        live.textContent = label + ' selected. ' + (guidance ? guidance.textContent.trim() : '');
      }

      if (opts.scroll === true) moveToFirstCard(state);
    }

    root.querySelectorAll('.aa-ritual-selector__tab, .aa-ritual-selector__panel').forEach(function (label) {
      label.addEventListener('click', function (event) {
        var forId = label.getAttribute('for');
        var radio = forId ? document.getElementById(forId) : null;
        var state = radio ? radio.value : null;
        if (!state) return;
        event.preventDefault();
        setPathway(state, { scroll: true });
      });
    });

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (radio.checked) setPathway(radio.value, { scroll: true });
      });
    });

    setPathway(current, { force: true });

    window.requestAnimationFrame(function () {
      readyForScroll = true;
    });
  }

  /**
   * Homepage pathway links: /pages/the-collection?pathway=release|remember|reign
   * selects the matching pathway once on initial load and brings the
   * #pathway-blends results into view (honouring the sticky-header offset via
   * scroll-margin-top). A missing or invalid value is ignored, leaving the
   * markup default (Release) selected. Reuses setPathway() through the native
   * radio change event so there is one pathway state function.
   */
  function enhancePathwayParam(root) {
    var raw = new URLSearchParams(window.location.search).get('pathway');
    if (!raw) return;
    var state = String(raw).toLowerCase();
    if (STATES.indexOf(state) === -1) return;

    var radio = root.querySelector('.aa-ritual-selector__radio[value="' + state + '"]');
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var anchor = root.querySelector('#pathway-blends') || root.querySelector('.aa-ritual-selector__cardsets');
    if (!anchor) return;

    // The blend cards and pathway image lazy-load and expand the layout after
    // first paint, so a single rAF scrollIntoView lands short. Fire once early,
    // then correct once more after the late reflow (and on window load), unless
    // the visitor has already started scrolling themselves.
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var interrupted = false;
    var markInterrupted = function () { interrupted = true; };
    ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, markInterrupted, { passive: true, once: true });
    });
    var toAnchor = function () {
      if (interrupted) return;
      anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    };
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(toAnchor);
    });
    window.setTimeout(toAnchor, 350);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', function () { window.setTimeout(toAnchor, 60); }, { once: true });
    }
  }

  /**
   * Stable deep link: /pages/the-collection?blend=<key> selects the
   * matching pathway and scrolls to that card once on initial load.
   */
  function enhanceDeepLink(root) {
    var params = new URLSearchParams(window.location.search);
    var deepLinkKey = params.get('blend');
    if (!deepLinkKey) return;

    var card = root.querySelector('[data-blend-card="' + deepLinkKey + '"]');
    if (!card) return;

    var cardset = card.closest('.aa-ritual-selector__cardset');
    if (cardset) {
      var stateMatch = cardset.className.match(/aa-ritual-selector__cardset--(\S+)/);
      if (stateMatch) {
        var stateRadio = root.querySelector('.aa-ritual-selector__radio[value="' + stateMatch[1] + '"]');
        if (stateRadio && !stateRadio.checked) {
          stateRadio.checked = true;
          stateRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    card.setAttribute('data-blend-highlight', '');
    window.requestAnimationFrame(function () {
      card.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
    });
  }

  document.querySelectorAll('.aa-ritual-selector').forEach(function (root) {
    enhance(root);
    enhancePathwayParam(root);
    enhanceDeepLink(root);
  });
})();
